import React, {
	useState,
	useEffect,
	useLayoutEffect,
	useRef,
	useMemo,
	Fragment,
	ReactNode,
	RefObject,
} from 'react';
import './BayViews.scss';

import clsx from 'clsx';
import { Dropdown, Modal, Form, Button } from 'react-bootstrap';
import {
	BsThreeDotsVertical,
	BsPlusLg,
	BsFillCaretDownFill,
	BsPencilFill,
	BsTrash3,
} from 'react-icons/bs';
import { MdContentCopy } from 'react-icons/md';

import BayTooltip from '../BayTooltip';

interface CustomToggleProps {
	editable?: boolean;
	onClick?: (e: React.MouseEvent) => void;
	isMore?: boolean;
	setOpenedId?: (id: string | null) => void;
	openedId?: string | null;
	id?: string;
	setShow?: () => void;
	onHandleClick?: () => void;
	children?: ReactNode;
	style?: React.CSSProperties;
}

const CustomToggle = React.forwardRef<HTMLSpanElement | HTMLButtonElement, CustomToggleProps>(
	(
		{
			editable,
			onClick,
			isMore,
			setOpenedId = () => {},
			openedId,
			id,
			setShow = () => {},
			onHandleClick = () => {},
			children,
			style,
		},
		ref,
	) =>
		!isMore ? (
			<span
				ref={ref as RefObject<HTMLSpanElement>}
				className="d-flex align-items-center justify-content-center"
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					setOpenedId(id === openedId ? null : id || null);
					//For close the more views dropdown
					onHandleClick();
				}}
			>
				<BsThreeDotsVertical />
			</span>
		) : (
			<Button
				ref={ref as RefObject<HTMLButtonElement>}
				className={`more-option me-3 pe-4 rounded-0 mb-2 d-flex align-items-center text-decoration-none${isMore ? ' more-option' : ''}${editable ? ' border-end' : ''}`}
				variant="link"
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					onClick?.(e);
					setShow();
				}}
			>
				<span>
					More
					<span className="ms-2">
						<BsFillCaretDownFill />
					</span>
				</span>
			</Button>
		),
);

export interface ViewData {
	id: string;
	viewname: string;
	active: boolean;
	isEditable: boolean;
	isDefaultView?: boolean;
	isModified?: boolean;
}

interface ViewRowProps {
	refs: React.MutableRefObject<React.RefObject<HTMLSpanElement>[]>;
	widthArray: number[];
	views: ViewData[];
	onClickHandler: (view: ViewData, currentViewCount: number) => void;
	handleActionClicked: (event: React.MouseEvent | React.SyntheticEvent, action: string, view: ViewData) => void;
	currentViewCount: number;
	t: (key: string) => string;
	setIsShow: (isShow: boolean) => void;
	openedId: string | null;
	setOpenedId: (id: string | null) => void;
	lastDefaultIndex: number;
	isAllowRestoreDefault: boolean;
	showViewNameTooltips?: boolean;
	dropdownMenuClass?: string;
	showDivider?: boolean;
	viewClassName?: string;
}

const ViewRow: React.FC<ViewRowProps> = ({
	refs,
	widthArray,
	views,
	onClickHandler,
	handleActionClicked,
	currentViewCount,
	t,
	setIsShow,
	openedId,
	setOpenedId,
	lastDefaultIndex,
	isAllowRestoreDefault,
	showViewNameTooltips = true,
	dropdownMenuClass = '',
	showDivider = true,
	viewClassName = 'lh-lg rounded-pill fw-light px-3 me-2 mb-2 border',
}) => {
	const ViewDropdown = useMemo(() => {
		// Memoize the component definition, but the component itself will receive `view` and `index` as props.
		const MemoizedDropdown: React.FC<{ view: ViewData; index: number }> = ({ view, index }) => {
			// Explicitly reference view and index as props to avoid linter warnings.
			if (!(view.isEditable || isAllowRestoreDefault)) return null;
			return (
				<Dropdown
					align="end"
					id={`view_dropdown_${index}`}
					show={openedId === view?.id}
					onSelect={(eventKey, event) => {
						event?.stopPropagation();
						event?.preventDefault();
						if (eventKey && event) {
							handleActionClicked(event as React.MouseEvent, eventKey, view);
						}
					}}
					onToggle={() => setOpenedId(null)}
				>
					<Dropdown.Toggle
						as={CustomToggle}
						id={view?.id}
						openedId={openedId}
						setOpenedId={setOpenedId}
						onHandleClick={() => setIsShow(false)}
					/>
					<Dropdown.Menu className={dropdownMenuClass} popperConfig={{ strategy: 'absolute' }} renderOnMount={false}>
						{!view.isEditable && view?.isDefaultView && (
							<BayTooltip
								disabled={view?.isModified}
								mouseEnterDelay={0.25}
								overlay="You cannot restore the default layout because no changes have been made to the layout of this view."
							>
								<span className="cursor-pointer">
									<Dropdown.Item
										as="span"
										className="d-flex align-items-center"
										disabled={!view?.isModified}
										eventKey="restoreDefaultLayout"
									>
										{t('restoreDefaultLayout')}
									</Dropdown.Item>
								</span>
							</BayTooltip>
						)}
						{view.isEditable && (
							<>
								<Dropdown.Item
									as="span"
									className="d-flex align-items-center"
									eventKey="rename"
								>
									<span className="me-2">
										<BsPencilFill />
									</span>
									{t('rename')}
								</Dropdown.Item>
								<Dropdown.Item
									as="span"
									className="d-flex align-items-center"
									eventKey="duplicate"
								>
									<span className="me-2">
										<MdContentCopy />
									</span>
									{t('duplicate')}
								</Dropdown.Item>
								<Dropdown.Item
									as="span"
									className="d-flex align-items-center"
									eventKey="delete"
								>
									<span className="me-2">
										<BsTrash3 />
									</span>
									{t('delete')}
								</Dropdown.Item>
							</>
						)}
					</Dropdown.Menu>
				</Dropdown>
			);
		};
		return MemoizedDropdown;
	}, [
		isAllowRestoreDefault,
		openedId,
		handleActionClicked,
		setOpenedId,
		setIsShow,
		t,
		dropdownMenuClass,
	]);

	return (
		<>
			{views.map((view, index) => {
				if (index <= currentViewCount - 1) {
					// Force "All" button tooltip to always show on the right side
					const isAllButton = index === 0;
					// Translated from rc-trigger's `align` object, which BayTooltip no longer
					// reads: `points: ['cl','cr']` (tooltip centre-left to target centre-right)
					// IS `placement: 'right'`; `offset: [8,0]` pushed it 8px further out, so the
					// default gap of 4 becomes 12; and `overflow: { adjustX: false }` -- the
					// "always show on the right side" requirement -- is `allowFlip: false`.
					// `shift()` still nudges it into view vertically, which is what
					// `adjustY: true` asked for.
					const tooltipProps = isAllButton
						? {
							placement: 'right' as const,
							arrowOffset: 12,
							allowFlip: false,
						}
						: {
							placement: 'bottom' as const,
						};

					return (
						<Fragment key={index}>
							<div
								className={`bay-view ${viewClassName} ${view.active ? 'bg-secondary border-secondary bay-view-active' : 'bg-100'} ${index === 0 ? ' all-button' : ''}${openedId === view?.id ? ' bay-view-menu-open' : ''}`}
								onClick={() => onClickHandler(view, currentViewCount)}
							>
								<BayTooltip
									disabled={!showViewNameTooltips}
									mouseEnterDelay={0.25}
									overlay={<span>{view.viewname}</span>}
									{...tooltipProps}
								>
									<span
										ref={refs.current[index]}
										className="view-name"
										role="button"
										style={{ ['--ele-width' as any]: `${widthArray[index]}px` }}
									>
										{view.viewname}
									</span>
								</BayTooltip>
								<ViewDropdown index={index} view={view} />
							</div>
							{/* Added a new prop to show or hide the divider based on 'showDivider' */}
							{(lastDefaultIndex === index && showDivider) && (
								<span className="lastDefaultView vertical-divider" />
							)}
						</Fragment>
					);
				}
				return null;
			})}
		</>
	);
};

export interface BayViewsProps {
	viewData: ViewData[];
	t: (key: string) => string;
	setActiveView?: (data: ViewData[], currentViewCount: number) => void;
	updateViewData: (action: string, view: ViewData | { viewname: string }) => void;
	editable?: boolean;
	triggerModal?: boolean;
	triggerModalType?: string;
	showModalData?: any;
	/** Called whenever the view modal closes (save, delete or cancel). */
	onModalClose?: () => void;
	isAllowRestoreDefault?: boolean;
	customModalContent?: boolean;
	addModalContent?: () => ReactNode;
	showViewNameTooltips?: boolean;
	dropdownMenuClass?: string;
	dropdownItemTextClassName?: string;
	showDivider?: boolean;
	/**
	 * Replaces the hardcoded `bay-views-row mt-4 d-flex flex-wrap
	 * align-items-center pb-3 border-bottom` className on the outer
	 * container. Bootstrap's `mt-4`/`pb-3`/`border-bottom` utilities compile
	 * with `!important` in this build, so a consumer needing different
	 * spacing/border can't win with specificity -- pass a full replacement
	 * string (keep `bay-views-row` in it -- BayViews.scss and the `.no-edit`
	 * modifier key off that class).
	 */
	className?: string;
	/**
	 * Replaces the hardcoded `lh-lg rounded-pill fw-light px-3 me-2 mb-2
	 * border` utility classes on each view pill. Same reasoning as
	 * `className` above -- these are Bootstrap utilities and compile
	 * !important, so consumers needing different padding/typography/spacing
	 * must replace this list rather than fight it. `bay-view` and the
	 * `bay-view-active`/`all-button`/`bg-*` state classes stay hardcoded
	 * (BayViews.scss keys off them for hover/active behavior).
	 */
	viewClassName?: string;
}

const BayViews: React.FC<BayViewsProps> = ({
	t,
	setActiveView,
	updateViewData,
	editable = false,
	viewData,
	triggerModal,
	triggerModalType,
	showModalData,
	onModalClose,
	isAllowRestoreDefault = false,
	customModalContent = false,
	addModalContent = () => {},
	showViewNameTooltips = true,
	dropdownMenuClass = '',
	dropdownItemTextClassName = '',
	showDivider = true,
	className = 'bay-views-row mt-4 d-flex flex-wrap align-items-center pb-3 border-bottom',
	viewClassName = 'lh-lg rounded-pill fw-light px-3 me-2 mb-2 border',
}) => {
	const [views, setViews] = useState<ViewData[]>(viewData ?? []);
	const [currentViewCount, setCurrentViewCount] = useState(4);
	const [showMore, setShowMore] = useState(false);
	const [showModal, setShowModal] = useState<{ show: boolean; view?: ViewData; currentViewCount?: number }>({ show: false });
	const [modalType, setModalType] = useState('');
	const [inputValue, setInputValue] = useState('');
	const [widthArray, setWidthArray] = useState<number[]>([]);
	const [isShow, setIsShow] = useState(false);
	const [openedId, setOpenedId] = useState<string | null>(null);

	const viewsRefs = useRef<React.RefObject<HTMLSpanElement>[]>(views?.map(() => React.createRef()));
	const parentElementRef = useRef<HTMLDivElement>(null);
	const widthUpdateTimerRef = useRef<number | null>(null);
	const measureRef = useRef<() => void>(() => {});

	const lastDefaultIndex =
		views?.length > 0
			? views.slice().reverse().findIndex((e) => e?.isDefaultView === true) !== -1
				? views.length - 1 - views.slice().reverse().findIndex((e) => e?.isDefaultView === true)
				: -1
			: -1;

	measureRef.current = () => {
		if (!Array.isArray(views)) return;

		setViewButtonsWidths();
		setViewsCount();
	};

	useEffect(() => {
		setViews(viewData);
		setViewButtonsWidths();
		// New data means the row is rebuilt - drop any dropdown left open.
		setOpenedId(null);
		setIsShow(false);
	}, [viewData]);

	useEffect(() => {
		if (showModalData) {
			handleOpenModal('create', showModalData);
		}
	}, [showModalData]);

	useLayoutEffect(() => {
		if (viewsRefs?.current?.length) {
			widthUpdateTimerRef.current = setTimeout(() => {
				setViewButtonsWidths();
			}, 500);
		}

		return () => {
			if (widthUpdateTimerRef.current) {
				clearTimeout(widthUpdateTimerRef.current);
				widthUpdateTimerRef.current = null;
			}
		};
	}, [viewsRefs, parentElementRef, views?.length]);

	// Observe only the container to avoid repeated measurements and dropdown flickering.
	useLayoutEffect(() => {
		const observer = new ResizeObserver(() => measureRef.current());

		if (parentElementRef.current !== null) {
			observer.observe(parentElementRef.current);
		}

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (triggerModal) {
			handleOpenModal(triggerModalType || 'create', viewData);
		}
	}, [triggerModal, triggerModalType, viewData]);

	const setViewsCount = () => {
		let count = 0;
		const offset = editable ? 1 : 0;
		const viewsCurrentWidths = widthArray.reduce((a, b) => a + b, 0);

		const parentWidth = parentElementRef?.current?.clientWidth || 0;

		count = Math.floor(parentWidth / 180);

		count = viewsCurrentWidths > parentWidth ? count - offset : count;

		if (views.length > count) {
			setShowMore(true);
		} else {
			setShowMore(false);
		}

		setCurrentViewCount(count);
	};

	const setViewButtonsWidths = () => {
		const tempArray: number[] = [];

		for (const element of viewsRefs.current) {
			if (element.current !== null) {
				tempArray.push(Number(element.current?.clientWidth));
			}
		}

		// Keep the previous array when nothing actually changed, so a measure
		// pass cannot by itself schedule another render.
		setWidthArray((previous) =>
			previous.length === tempArray.length &&
			previous.every((width, i) => width === tempArray[i])
				? previous
				: tempArray,
		);
	};

	const handleActionClicked = (event: React.MouseEvent | React.SyntheticEvent, action: string, view: ViewData) => {
		event?.preventDefault();

		if (action !== 'duplicate' && action !== 'restoreDefaultLayout') {
			handleOpenModal(action, view);
		} else {
			updateViewData(action, view);
		}
		setIsShow(false);
		setOpenedId(null);
	};

	const onClickHandler = (view: ViewData, currentViewCount: number) => {
		// Activating a view must never leave an ellipsis / "More" menu behind.
		setOpenedId(null);
		setIsShow(false);

		if (views?.find((el) => el.id === view.id)?.active) {
			return;
		}

		const newData = views.map((el) => {
			if (el.id === view.id) {
				return { ...view, active: true };
			} else {
				return { ...el, active: false };
			}
		});

		setViews(newData);

		if (setActiveView) {
			setActiveView(newData, currentViewCount);
		}
	};

	const handleSaveViewName = () => {
		if (modalType === 'create') {
			updateViewData('create', {
				viewname: inputValue,
			});
		} else {
			const index = views.findIndex((e) => e.id === showModal.view?.id);
			const newViews = [...views];
			newViews[index].viewname = inputValue;
			setViews(newViews);
			updateViewData('rename', newViews[index]);
		}
		handleCloseModal();
	};

	const handleDeleteView = () => {
		if (showModal.view) {
			updateViewData('delete', showModal.view);
		}
		handleCloseModal();
	};

	const handleCloseModal = () => {
		setShowModal({ show: false });
		setInputValue('');
		onModalClose?.();
	};

	const handleOpenModal = (action: string, view: any) => {
		if (action === 'rename') {
			setInputValue(view?.viewname);
		} else {
			setInputValue('');
		}
		setModalType(action);
		setShowModal({ view, show: true, currentViewCount });
	};

	return (
		<div
			ref={parentElementRef}
			className={`${className}${
				!editable ? ' no-edit' : ''
			}`}
			id="views_container"
		>
			{views?.length > 0 ? (
				<ViewRow
					currentViewCount={currentViewCount}
					dropdownMenuClass={dropdownMenuClass}
					handleActionClicked={handleActionClicked}
					isAllowRestoreDefault={isAllowRestoreDefault}
					lastDefaultIndex={lastDefaultIndex}
					openedId={openedId}
					refs={viewsRefs}
					setIsShow={setIsShow}
					setOpenedId={setOpenedId}
					showDivider={showDivider}
					showViewNameTooltips={showViewNameTooltips}
					t={t}
					views={views.map((e) => e).slice(0, currentViewCount)}
					viewClassName={viewClassName}
					widthArray={widthArray}
					onClickHandler={onClickHandler}
				/>
			) : null}
			{views?.length && showMore ? (
				<Dropdown
					align="end"
					id="view_dropdown_more"
					show={isShow}
					onToggle={() => setIsShow(false)}
				>
					<Dropdown.Toggle
						isMore
						as={CustomToggle}
						editable={editable}
						setShow={() => {
							//For close the per view ellipsis dropdown
							setOpenedId(null);
							setIsShow(!isShow);
						}}
					/>
					<Dropdown.Menu
						className="scrollbar-sm"
						popperConfig={{ strategy: 'absolute' }}
						renderOnMount={false}
						style={{
							maxWidth: 320,
							maxHeight: 280,
							overflowY: 'auto',
							zIndex: 3,
						}}
					>
						{views
							.map((e) => e)
							.slice(currentViewCount)
							.map((view, index) => {
								return (
									<Dropdown.Item
										key={index}
										active={view.active}
										as="span"
										className="view-dropdown-item d-flex align-items-center justify-content-between py-2 cursor-pointer"
										eventKey={view.id}
										onClick={() => onClickHandler(view, currentViewCount)}
									>
										<BayTooltip
											disabled={!showViewNameTooltips}
											mouseEnterDelay={0.5}
											mouseLeaveDelay={0.05}
											overlay={view.viewname}
											placement="top"
										>
											<span className={clsx('text-truncate', dropdownItemTextClassName)}>{view.viewname}</span>
										</BayTooltip>
										{view.isEditable && (
											<div>
												<BayTooltip overlay={t('rename')} placement="top">
													<span
														className="ms-3 p-1 view-more-dropdown-item"
														onClick={(e) => {
															e.preventDefault();
															e.stopPropagation();
															handleActionClicked(e, 'rename', view);
														}}
													>
														<BsPencilFill className="dropdown-item-icon mb-1" />
													</span>
												</BayTooltip>
												<BayTooltip overlay={t('duplicate')} placement="top">
													<span
														className="ms-3 p-1 view-more-dropdown-item"
														onClick={(e) => {
															e.preventDefault();
															e.stopPropagation();
															handleActionClicked(e, 'duplicate', view);
														}}
													>
														<MdContentCopy className="dropdown-item-icon mb-1" />
													</span>
												</BayTooltip>
												<BayTooltip overlay={t('delete')} placement="top">
													<span
														className="ms-3 p-1 view-more-dropdown-item"
														onClick={(e) => {
															e.preventDefault();
															e.stopPropagation();
															handleActionClicked(e, 'delete', view);
														}}
													>
														<BsTrash3 className="dropdown-item-icon mb-1" />
													</span>
												</BayTooltip>
											</div>
										)}
									</Dropdown.Item>
								);
							})}
					</Dropdown.Menu>
				</Dropdown>
			) : null}
			{editable && (
				<Button
					className="add-view me-2 mb-2 d-flex align-items-center text-decoration-none"
					variant="link"
					onClick={(e) => handleActionClicked(e, 'create', {} as ViewData)}
				>
					<BsPlusLg />
					<span className="ms-1">Add View</span>
				</Button>
			)}
			{/* Rename and create modal */}
			<Modal className="bay-views-modal" show={showModal.show} onHide={() => handleCloseModal()}>
				<Modal.Header closeButton>
					<Modal.Title>
						{modalType === 'delete'
							? 'Delete View'
							: modalType === 'create'
								? 'Add New View'
								: 'Rename View'}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{modalType === 'create' && (
						customModalContent ? <>{addModalContent()}</> : (
							<p>{t('addViewsContent')}</p>
						)
					)}
					{modalType === 'delete' ? (
						<>
							<p>{t('deleteViewsContent')}</p>
							<Form.Group className="my-2">
								<Form.Label className="fw-semibold" htmlFor="input_view_name">
									View Name
								</Form.Label>
								<Form.Control
									disabled
									readOnly
									autoComplete="off"
									className="my-2"
									defaultValue={showModal.view?.viewname}
									id="input_view_name"
									type="text"
								/>
							</Form.Group>
						</>
					) : (
						<Form.Group>
							<Form.Label className="fw-semibold" htmlFor="input_view_name">
								View Name
							</Form.Label>
							<Form.Control
								required
								autoComplete="off"
								id="input_view_name"
								maxLength={64}
								placeholder="New View Name"
								type="text"
								value={inputValue}
								onChange={(e) => setInputValue(e.target.value)}
							/>
						</Form.Group>
					)}
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="outline-secondary"
						onClick={() => handleCloseModal()}
					>
						Cancel
					</Button>
					{modalType === 'delete' ? (
						<Button variant="danger" onClick={() => handleDeleteView()}>
							Delete
						</Button>
					) : (
						<Button
							disabled={inputValue?.length <= 0}
							variant="primary"
							onClick={() => handleSaveViewName()}
						>
							{modalType === 'create' ? 'Create' : 'Update'}
						</Button>
					)}
				</Modal.Footer>
			</Modal>
		</div>
	);
};

export default BayViews;
